import React from "react";
import styled, { useTheme } from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { getBoxShadow, isMeleeItem } from "@utils/product/productValidators";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { ProductCategory, ViewType } from "@utils/constants";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import currency from "currency.js";
import { getRequestType, RequestTypes } from "./utils/utils";
import LineViewContainer from "./utils/LineView";
import { useAllowQuantityOnBulk } from "@redux/slices/globalSlice";

type TotalPriceCardProps = {
  stockItemRequest: StockItemRequestClass;
  productType: {
    productType: string;
    bulkProductName: string;
    productIcon: string;
    theme: string;
  };
  hasChildren: boolean;
  childRequest?: boolean;
  hasAnyOfferDetails?: boolean;
  showListPrice?: boolean;
  orderCartRequest?: boolean;
  isBuilderItem?: boolean;
  isLooseParcel?: boolean;
};

const TotalPriceCard = ({
  stockItemRequest,
  productType,
  hasChildren,
  childRequest,
  hasAnyOfferDetails,
  orderCartRequest = false,
  isBuilderItem,
  isLooseParcel,
}: TotalPriceCardProps) => {
  const orderCartPricing = stockItemRequest?.order_cart_pricing;
  const discountOrderCart = orderCartPricing?.discount_total;
  const subTotalOrderCart = orderCartPricing?.listed_items_subtotal;
  const totalPriceAfterDiscount = orderCartPricing?.items_total_after_discount;
  const hasAmount = (value: unknown) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value));
  const hasAppliedPromo =
    Boolean(stockItemRequest?.promo_code_label) ||
    (hasAmount(discountOrderCart) && Number(discountOrderCart) !== 0) ||
    (hasAmount(stockItemRequest?.promo_discount_amount) &&
      Number(stockItemRequest.promo_discount_amount) !== 0);
  const promoDiscountAmount = hasAmount(discountOrderCart)
    ? Number(discountOrderCart)
    : hasAmount(stockItemRequest?.promo_discount_amount)
    ? Number(stockItemRequest.promo_discount_amount)
    : 0;

  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const precision =
    productType.productType === ProductCategory.JEWELRY ||
    (userInfo?.roundUpPrices() &&
      !isMeleeItem(stockItemRequest?.[productType.productType]))
      ? 0
      : 2;
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const hidePriceSection =
    (stockItemRequest.view_type !== ViewType.BUYER &&
      childRequest &&
      stockItemRequest?.quote_details?.total_offer_price_applied &&
      !hasAnyOfferDetails) ||
    isBuilderItem;

  let isOfferPriceApplied =
    currency(stockItemRequest.total_listed_price, {
      symbol: stockItemRequest[productType.productType]?.currency_symbol,
      precision,
    }).format() !==
    currency(stockItemRequest.offer_total_sales_price, {
      symbol: stockItemRequest[productType.productType]?.currency_symbol,
      precision,
    }).format();
  let requestBuyView =
    getRequestType(stockItemRequest?.request_type) === RequestTypes.Buy &&
    isOfferPriceApplied;

  const ShippingMethodView = () => {
    return (
      <>
        <DiscountCard>
          {(subTotalOrderCart ||
            stockItemRequest?.shipping_charges ||
            stockItemRequest?.promo_discount_amount) && (
            <>
              <InnerContainer>
                <LeftText
                  isMobile={theme.isMobile}
                  customWidth={theme.isMobile ? 60 : 45}
                >
                  <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                    {t(
                      "requestHistoryDetail.shippingCharges.subtotal.text",
                      "Subtotal"
                    )}
                  </VDBText>
                </LeftText>
                <RightText>
                  <VDBText
                    type={typography["H3-M"]}
                    isBold={true}
                    color={theme["core-05"]}
                  >
                    {currency(
                      subTotalOrderCart
                        ? Number(subTotalOrderCart)
                        : Number(stockItemRequest?.offer_total_sales_price) +
                            Number(
                              stockItemRequest?.promo_discount_amount || 0
                            ) -
                            Number(stockItemRequest?.shipping_charges || 0),
                      {
                        symbol: orderCartRequest
                          ? stockItemRequest?.children?.[0]?.currency_symbol
                          : stockItemRequest[productType.productType]
                              ?.currency_symbol,
                        precision: precision,
                      }
                    ).format()}
                  </VDBText>
                </RightText>
              </InnerContainer>
              <Spacer size={SpaceType.Basic} />
            </>
          )}
          {stockItemRequest?.shipping_charges && (
            <>
              <InnerContainer>
                <LeftText
                  isMobile={theme.isMobile}
                  customWidth={theme.isMobile ? 60 : 55}
                >
                  <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                    {t("requestHistoryDetail.shippingCharges.text")}
                    {` (${stockItemRequest?.shipping_method?.name})`}
                  </VDBText>
                </LeftText>
                <RightText>
                  <VDBText
                    type={typography["H3-M"]}
                    isBold={true}
                    color={theme["core-05"]}
                  >
                    {currency(stockItemRequest?.shipping_charges, {
                      symbol: orderCartRequest
                        ? stockItemRequest?.children?.[0]?.currency_symbol
                        : stockItemRequest[productType.productType]
                            ?.currency_symbol,
                      precision: precision,
                    }).format()}
                  </VDBText>
                </RightText>
              </InnerContainer>
              <Spacer size={SpaceType.Basic} />
            </>
          )}
          {hasAppliedPromo && (
            <>
              <InnerContainer>
                <LeftText
                  isMobile={theme.isMobile}
                  customWidth={theme.isMobile ? 60 : 55}
                >
                  <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                    {t(
                      "requestHistoryDetail.shippingCharges.discount.text",
                      "Discount"
                    )}{" "}
                    {stockItemRequest?.promo_code_label
                      ? `(${stockItemRequest?.promo_code_label})`
                      : ""}
                  </VDBText>
                </LeftText>
                <RightText>
                  <VDBText
                    type={typography["H3-M"]}
                    isBold={true}
                    color={theme["core-05"]}
                  >
                    {"-"}
                    {currency(promoDiscountAmount, {
                      symbol: orderCartRequest
                        ? stockItemRequest?.children?.[0]?.currency_symbol
                        : stockItemRequest[productType.productType]
                            ?.currency_symbol,
                      precision: precision,
                    }).format()}
                  </VDBText>
                </RightText>
              </InnerContainer>
            </>
          )}
        </DiscountCard>
        <LineViewContainer background={"core-03"} />
      </>
    );
  };

  return (
    <>
      {hidePriceSection ? (
        <></>
      ) : allowQuantityOnBulk &&
        productType?.productType === ProductCategory.JEWELRY ? (
        <CardContainer>
          <StatusBottomContainer>
            <StoreAttributeContainer>
              <LeftText isMobile={theme.isMobile}>
                <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                  {t("requestHistoryDetail.totalPrice.text")}
                </VDBText>
              </LeftText>
              <RightText>
                <VDBText
                  type={typography["H3-M"]}
                  isBold={true}
                  color={theme["core-05"]}
                >
                  {currency(stockItemRequest?.total_amount, {
                    symbol:
                      stockItemRequest[productType.productType]
                        ?.currency_symbol ?? stockItemRequest?.currency_symbol,
                    precision: precision,
                  }).format()}
                </VDBText>
              </RightText>
            </StoreAttributeContainer>
          </StatusBottomContainer>
        </CardContainer>
      ) : (
        <CardContainer>
          <StatusBottomContainer>
            {stockItemRequest.view_type === ViewType.BUYER || requestBuyView ? (
              hasChildren ? (
                currency(stockItemRequest.total_listed_price, {
                  symbol:
                    stockItemRequest[productType.productType]?.currency_symbol,
                  precision,
                }).format() ===
                currency(stockItemRequest.offer_total_sales_price, {
                  symbol:
                    stockItemRequest[productType.productType]?.currency_symbol,
                  precision,
                }).format() ? (
                  <StoreAttributeContainer>
                    <LeftText isMobile={theme.isMobile}>
                      <VDBText
                        type={typography["H3-R"]}
                        color={theme["core-06"]}
                      >
                        {!isOfferPriceApplied
                          ? t("requestHistoryDetail.totalPrice.text")
                          : t("requestHistoryDetail.totalOfferPrice.text")}
                      </VDBText>
                    </LeftText>
                    <RightText>
                      <VDBText
                        type={typography["H3-M"]}
                        isBold={true}
                        color={theme["core-05"]}
                      >
                        {currency(stockItemRequest?.offer_total_sales_price, {
                          symbol:
                            stockItemRequest[productType.productType]
                              ?.currency_symbol,
                          precision: precision,
                        }).format()}
                      </VDBText>
                    </RightText>
                  </StoreAttributeContainer>
                ) : (
                  <>
                    {stockItemRequest.total_listed_price && !isLooseParcel && (
                      <StoreAttributeContainer>
                        <LeftText isMobile={theme.isMobile}>
                          <VDBText
                            type={typography["H3-R"]}
                            color={theme["core-06"]}
                          >
                            {t("requestHistoryDetail.totalListedPrice.text")}
                          </VDBText>
                        </LeftText>
                        <RightText>
                          <VDBText
                            type={typography["H3-M"]}
                            isBold={true}
                            color={theme["core-05"]}
                          >
                            {currency(stockItemRequest?.total_listed_price, {
                              symbol:
                                stockItemRequest[productType.productType]
                                  ?.currency_symbol,
                              precision: precision,
                            }).format()}
                          </VDBText>
                        </RightText>
                      </StoreAttributeContainer>
                    )}
                    {(hasAppliedPromo ||
                      stockItemRequest?.shipping_charges) && (
                      <ShippingMethodView />
                    )}
                    <StoreAttributeContainer>
                      <LeftText isMobile={theme.isMobile}>
                        <VDBText
                          type={typography["H3-R"]}
                          color={theme["core-06"]}
                        >
                          {isLooseParcel
                            ? t("requestHistoryDetail.totalEstimatedPrice.text")
                            : !stockItemRequest.total_listed_price
                            ? t("requestHistoryDetail.totalPrice.text")
                            : t("requestHistoryDetail.totalOfferPrice.text")}
                        </VDBText>
                      </LeftText>
                      <RightText>
                        <VDBText
                          type={typography["H3-M"]}
                          isBold={true}
                          color={theme["core-05"]}
                        >
                          {currency(stockItemRequest?.offer_total_sales_price, {
                            symbol: orderCartRequest
                              ? isBuilderItem
                                ? stockItemRequest?.currency_symbol
                                : stockItemRequest?.children?.[0]
                                    ?.currency_symbol
                              : stockItemRequest[productType.productType]
                                  ?.currency_symbol,
                            precision: precision,
                          }).format()}
                        </VDBText>
                      </RightText>
                    </StoreAttributeContainer>
                  </>
                )
              ) : currency(
                  stockItemRequest[productType.productType]?.total_sales_price,
                  {
                    symbol:
                      stockItemRequest[productType.productType]
                        ?.currency_symbol,
                    precision,
                  }
                ).format() ===
                currency(stockItemRequest.offer_total_sales_price, {
                  symbol:
                    stockItemRequest[productType.productType]?.currency_symbol,
                  precision,
                }).format() ? (
                <StoreAttributeContainer>
                  <LeftText isMobile={theme.isMobile}>
                    <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                      {t("requestHistoryDetail.totalPrice.text")}
                    </VDBText>
                  </LeftText>
                  <RightText>
                    <VDBText
                      type={typography["H3-M"]}
                      isBold={true}
                      color={theme["core-05"]}
                    >
                      {allowQuantityOnBulk &&
                      productType?.productType === ProductCategory.JEWELRY
                        ? currency(
                            stockItemRequest?.offer_total_sales_price *
                              (stockItemRequest?.quantity || 1),
                            {
                              symbol:
                                stockItemRequest[productType.productType]
                                  ?.currency_symbol,
                              precision: precision,
                            }
                          ).format()
                        : currency(stockItemRequest?.offer_total_sales_price, {
                            symbol:
                              stockItemRequest[productType.productType]
                                ?.currency_symbol,
                            precision: precision,
                          }).format()}
                    </VDBText>
                  </RightText>
                </StoreAttributeContainer>
              ) : (
                <>
                  {!isLooseParcel && (
                    <StoreAttributeContainer>
                      <LeftText isMobile={theme.isMobile}>
                        <VDBText
                          type={typography["H3-R"]}
                          color={theme["core-06"]}
                        >
                          {!stockItemRequest?.offer_total_sales_price
                            ? t("requestHistoryDetail.totalPrice.text")
                            : t("requestHistoryDetail.totalListedPrice.text")}
                        </VDBText>
                      </LeftText>
                      <RightText>
                        <VDBText
                          type={typography["H3-M"]}
                          isBold={true}
                          color={theme["core-05"]}
                        >
                          {currency(
                            stockItemRequest[productType.productType]
                              ?.total_sales_price,
                            {
                              symbol:
                                stockItemRequest[productType.productType]
                                  ?.currency_symbol,
                              precision: precision,
                            }
                          ).format()}
                        </VDBText>
                      </RightText>
                    </StoreAttributeContainer>
                  )}
                  {stockItemRequest?.offer_total_sales_price &&
                    Number(stockItemRequest?.offer_total_sales_price) > 0 && (
                      <StoreAttributeContainer>
                        <LeftText isMobile={theme.isMobile}>
                          <VDBText
                            type={typography["H3-R"]}
                            color={theme["core-06"]}
                          >
                            {isLooseParcel
                              ? t(
                                  "requestHistoryDetail.totalEstimatedPrice.text"
                                )
                              : t("requestHistoryDetail.totalOfferPrice.text")}
                          </VDBText>
                        </LeftText>
                        <RightText>
                          <VDBText
                            type={typography["H3-M"]}
                            isBold={true}
                            color={theme["core-05"]}
                          >
                            {currency(
                              stockItemRequest?.offer_total_sales_price,
                              {
                                symbol:
                                  stockItemRequest[productType.productType]
                                    ?.currency_symbol,
                                precision: precision,
                              }
                            ).format()}
                          </VDBText>
                        </RightText>
                      </StoreAttributeContainer>
                    )}
                </>
              )
            ) : (
              <>
                {(hasAppliedPromo || stockItemRequest?.shipping_charges) && (
                  <ShippingMethodView />
                )}
                <StoreAttributeContainer>
                  <LeftText isMobile={theme.isMobile}>
                    <VDBText type={typography["H3-R"]} color={theme["core-06"]}>
                      {t("requestHistoryDetail.totalPrice.text", "Total Price")}
                    </VDBText>
                  </LeftText>
                  <RightText>
                    <VDBText
                      type={typography["H3-M"]}
                      isBold={true}
                      color={theme["core-05"]}
                    >
                      {allowQuantityOnBulk &&
                      productType?.productType === ProductCategory.JEWELRY
                        ? currency(
                            (hasChildren || childRequest
                              ? stockItemRequest?.offer_total_sales_price ??
                                stockItemRequest?.total_listed_price
                              : stockItemRequest[productType.productType]
                                  ?.total_sales_price) *
                              (stockItemRequest?.quantity || 1),
                            {
                              symbol: orderCartRequest
                                ? stockItemRequest?.currency_symbol
                                : stockItemRequest[productType.productType]
                                    ?.currency_symbol,
                              precision: precision,
                            }
                          ).format()
                        : currency(
                            totalPriceAfterDiscount
                              ? Number(totalPriceAfterDiscount)
                              : hasChildren || childRequest
                              ? stockItemRequest?.offer_total_sales_price ??
                                stockItemRequest?.total_listed_price
                              : stockItemRequest[productType.productType]
                                  ?.total_sales_price,
                            {
                              symbol: orderCartRequest
                                ? stockItemRequest?.currency_symbol
                                : stockItemRequest[productType.productType]
                                    ?.currency_symbol,
                              precision: precision,
                            }
                          ).format()}
                    </VDBText>
                  </RightText>
                </StoreAttributeContainer>
              </>
            )}
          </StatusBottomContainer>
        </CardContainer>
      )}
      <Spacer size={SpaceType.Double} />
    </>
  );
};

export default TotalPriceCard;

const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const StatusBottomContainer = styled.View`
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
  gap: ${SpaceType.Basic}px;
`;

const StoreAttributeContainer = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Double}px;
  flex-direction: row;
  justify-content: space-between;
`;

const DiscountCard = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Double}px;
`;

const InnerContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const LeftText = styled.View<{
  isMobile?: boolean;
  customWidth?: number;
}>`
  width: ${({ isMobile, customWidth }) =>
    customWidth ? customWidth : isMobile ? 40 : 18}%;
`;

const RightText = styled.View`
  max-width: 800px;
`;
